import {
  DateField,
  List,
  Labeled,
  TextField,
  useNotify,
  useRedirect,
  useTranslate,
  Show,
  SimpleShowLayout,
  Toolbar,
  useDataProvider,
  useRecordContext,
  ReferenceField,
  useListContext,
  EmailField,
  FunctionField,
  WrapperField,
  UrlField,
  ImageField,
  useRefresh,
  SimpleForm,
  DateInput,
  Edit,
  required,
  DeleteWithConfirmButton,
  SaveButton,
  useGetOne,
  FormDataConsumer,
  NumberInput,
  minValue,
  Loading,
  BooleanInput,
  ReferenceManyField,
  Datagrid
} from 'react-admin';
import React, { FC, useEffect, useState, useMemo } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Link as MuiLink } from '@mui/material';
import { IconButton, Stack, Typography, Tooltip, Snackbar, InputAdornment } from '@mui/material';
import { FieldValues } from 'react-hook-form';
import EnumTextField from '../common_modules/components/EnumTextField';
import useErrorHandler from '../common_modules/hooks/useErrorHandler';
import { Box, Card, CardContent, CardMedia, Grid, Badge, Theme, Button, useMediaQuery } from '@mui/material';
import dataProvider from '../dataProvider';
import ZoomableImageField from '../common_modules/components/ZoomableImageField';
import PostalCodeField from '../common_modules/components/PostalCodeField';
import FlexibleListLayout from '../common_modules/components/FlexibleListLayout';
import PercentNumberField from '../common_modules/components/PercentNumberField';
import PercentNumberInput from '../common_modules/components/PercentNumberInput';
import NumericRadioButtonGroupInput from '../common_modules/components/NumericRadioButtonGroupInput';
import { Choice, INVALD_FLAG } from '../common_modules/constants/choices';
import { addDays, startOfDay, format, getDate, lastDayOfMonth, isSameDay, endOfDay } from 'date-fns';

const storedEcSiteType = localStorage.getItem('ecSiteType');

/**
 * クーポン機能がサポートされているかチェックする
 * @param ecSiteType ECサイトタイプ（文字列）
 * @returns true: クーポン機能あり
 */
const isCouponSupported = (ecSiteType: string | null): boolean => {
  return true;
};

/**
 * クーポンの手動作成が必要かチェックする
 * @param ecSiteType ECサイトタイプ（文字列）
 * @returns true: 手動作成が必要、false: 自動生成対応
 */
const isCouponManualCreationRequired = (ecSiteType: string | null): boolean => {
  // 自動生成（1: Shopify, 3: STORES, 4: カラーミーショップ, 8: makeshop）
  // 上記以外（2: BASE, 5: EC-CUBE, など）が手動作成対象
  return ecSiteType !== '1' && ecSiteType !== '3' && ecSiteType !== '4' && ecSiteType !== '8';
};

/**
 * TODO: ECサイトを画像表示にするサンプル
 */
const ClientCard = ({ client }: { client: any }) => {
  const [imageUrl, setImageUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    const fetchImage = async () => {
      try {
        const imageBlob = await dataProvider.getBlob(`clients/${client.id}/image`);
        const imageObjectUrl = URL.createObjectURL(imageBlob);
        setImageUrl(imageObjectUrl);
      } catch (error) {
        console.error('Error fetching image:', error);
      }
    };
    fetchImage();
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [client.id, imageUrl]);

  return (
    <Card sx={{ width: '180px', margin: 'auto' }}>
      <CardMedia component="img" height="150" image={imageUrl} alt={client.name} />
      <CardContent>
        <Typography gutterBottom variant="h5" component="div">
          {client.name}
        </Typography>
      </CardContent>
    </Card>
  );
};

const ClientGrid = () => {
  const { data, isLoading } = useListContext();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <Grid container spacing={2}>
      {data?.map((client) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={client.id}>
          <ClientCard client={client} />
        </Grid>
      ))}
    </Grid>
  );
};

interface EnumTextFieldProps {
  source: string;
  label?: string;
  translationKeyPrefix: string;
}

const StatusWithIndicatorField: React.FC<EnumTextFieldProps> = (props) => {
  const record = useRecordContext();
  const isUnread = record && record.clientReadFlag === false;
  const enumField = <EnumTextField {...props} />;
  if (isUnread) {
    return (
      <Badge
        color="error"
        variant="dot"
        // バッジの位置を左上に設定
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
      >
        {enumField}
      </Badge>
    );
  }
  return enumField;
};

const ChildPartnerExpand = () => {
  const translate = useTranslate();
  const record = useRecordContext();

  if (!record || !record.hasChildPartners) return null;

  return (
    <Box sx={{ padding: '20px 24px', backgroundColor: '#fcfcfc', overflowX: 'auto' }}>     
      <ReferenceManyField
        reference="affiliates"
        target="parentPartnerId"
        source="partnerId"
        pagination={false}
      >
        <Datagrid bulkActionButtons={false} style={{ width: '100%' }}>
          <StatusWithIndicatorField
            source="applyStatus"
            label={translate('resources.affiliates.fields.applyStatus')}
            translationKeyPrefix="values.applyStatus"
          />
          <TextField source="partnerId" />
          <TextField source="id" label={translate('resources.affiliates.fields.id')} />
          <TextField source="name" />
          <FunctionField
            source="partnerReward"
            render={(record) => (record.partnerReward != null ? `${record.partnerReward}%` : '')}
          />
          <DateField showTime={true} source="expirationDate" />
          <DateField source="createdAt" label={translate('resources.affiliates.fields.createdAt')} showTime />
          <DateField
            source="approvalStatusChangedAt"
            label={translate('resources.affiliates.fields.approvalStatusChangedAt')}
            showTime
          />
          <EnumTextField source="invalidFlag" translationKeyPrefix="values.invalidFlag" />
          <EnumTextField source="accountInvalidFlag" translationKeyPrefix="values.accountInvalidFlag" />
        </Datagrid>
      </ReferenceManyField>
    </Box>
  );
};

export const AffiliatesList = () => {
  const translate = useTranslate();
  return (
    <List
      resource="affiliates"
      exporter={false}
      sort={{ field: 'applyStatus', order: 'ASC' }}
      sx={{
        '& .MuiPaper-root': {
          boxShadow: {
            xs: 'none',
            sm: 'none',
            md: '0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)'
          }
        },
        '& .RaList-main': {
          marginTop: { sm: '8px', md: 0 }
        },
        '& .RaList-actions': {
          height: { xs: 0, sm: 0 }
        }
      }}
    >
      <FlexibleListLayout 
        rowClick="show" 
        bulkActionButtons={false}
        expand={<ChildPartnerExpand />}
        isRowExpandable={record => record.hasChildPartners}
      >
        <StatusWithIndicatorField
          source="applyStatus"
          label={translate('resources.affiliates.fields.applyStatus')}
          translationKeyPrefix="values.applyStatus"
        />
        <TextField source="partnerId" />
        <FunctionField
          label={translate('resources.affiliates.fields.partnerClass')}
          sortBy="partnerClass"
          render={(record: any) => record.parentPartnerId ? '2次' : '1次'}
        />
        <ReferenceField source="parentPartnerId" reference="partners" emptyText='-' label={translate('resources.affiliates.fields.parentPartner')}>
          <TextField source="name" />
        </ReferenceField>
        <TextField source="id" label={translate('resources.affiliates.fields.id')} />
        <TextField source="name" />
        <FunctionField
          source="partnerReward"
          render={(record) => (record.partnerReward != null ? `${record.partnerReward}%` : '')}
        />
        <DateField showTime={true} source="expirationDate" />
        <DateField source="createdAt" label={translate('resources.affiliates.fields.createdAt')} showTime />
        <DateField
          source="approvalStatusChangedAt"
          label={translate('resources.affiliates.fields.approvalStatusChangedAt')}
          showTime
        />
        <ReferenceField
          source="approvalStatusChanger"
          reference="references/accounts"
          label={translate('resources.affiliates.fields.approvalStatusChanger')}
          link={false}
        >
          <TextField source="name" />
        </ReferenceField>
        <FunctionField
          label={translate('resources.affiliates.fields.rewardPeriod')}
          render={(record: any) => {
            if (record.rewardPeriodFrag === 1 && record.rewardPeriodMonths > 0) {
              return `${record.rewardPeriodMonths}ヶ月`;
            }
            return '無期';
          }}
          sortable={false}
        />
        <EnumTextField source="invalidFlag" translationKeyPrefix="values.invalidFlag" />
        <EnumTextField source="accountInvalidFlag" translationKeyPrefix="values.accountInvalidFlag" />
      </FlexibleListLayout>
    </List>
  );
};

const EDIT_APPLY_STATUS: Choice[] = [
  { id: 2, name: '承認' },
  { id: 3, name: '却下' }
];

interface CopyableTextFieldProps {
  source: string;
  label?: string;
}

const CopyableTextField: FC<CopyableTextFieldProps> = (props) => {
  const { source } = props; // label や resource はここでは直接使わない
  const record = useRecordContext(props);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const translate = useTranslate();

  if (!record) {
    return null;
  }

  const value = record[source];

  const handleCopy = async () => {
    if (value !== null && value !== undefined) {
      try {
        await navigator.clipboard.writeText(String(value));
        setOpenSnackbar(true);
      } catch (err) {
        console.error('クリップボードへのコピーに失敗しました:', err);
      }
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  // ★ Labeled コンポーネントを削除し、Stack を直接返す
  return (
    <>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography component="span" variant="body2">
          {value === null || value === undefined ? '' : String(value)}
        </Typography>
        {value !== null && value !== undefined && String(value).trim() !== '' && (
          <Tooltip title={translate('ra.action.copy')}>
            <IconButton size="small" onClick={handleCopy} aria-label={translate('ra.action.copy')} sx={{ p: 0.5 }}>
              <ContentCopyIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
        message={translate('ra.message.copied')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};

import { useFormContext } from 'react-hook-form';

const CustomDeleteButton = () => {
  const record = useRecordContext();

  if (!record) return null;

  return (
    <DeleteWithConfirmButton
      mutationMode="pessimistic"
      label="削除"
      confirmTitle={`${record.name}を削除`}
      confirmContent="本当に削除しますか？"
      redirect="list"
    />
  );
};

const CustomToolbar = () => {
  const { getValues } = useFormContext();
  const isDisabled = getValues('applyStatus') === 1;

  return (
    <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <SaveButton disabled={isDisabled} />
      <CustomDeleteButton />
    </Toolbar>
  );
};

// ▼ Propsの型定義
interface AffiliateEditContentProps {
  cachedClient: any;
}

// ▼ 親から cachedClient を受け取る形に変更
export const AffiliateEditContent = ({ cachedClient }: AffiliateEditContentProps) => {
  const translate = useTranslate();
  const record = useRecordContext();
  const handleError = useErrorHandler();
  const notify = useNotify();
  const redirect = useRedirect();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  const clientId = localStorage.getItem('clientId');
  const storedEcSiteType = localStorage.getItem('ecSiteType');
  const [ecSiteAdminUrl, setEcSiteAdminUrl] = useState<string | null>(null);

  // 既読フラグの更新 (Effect)
  useEffect(() => {
    const markAsRead = async () => {
      if (record && (record.clientReadFlag === false || record.successfeeReadFlag === null)) {
        await dataProvider.setRead(record.id);
      }
    };
    markAsRead();
  }, [record, dataProvider]);

  // 管理画面URL (ecSiteAdminUrl) の決定ロジック (Effect)
  useEffect(() => {
    // 非同期処理を行うための関数を定義
    const determineUrl = async () => {
      // 1. まずキャッシュ(cachedClient)かlocalStorageからサイトタイプを特定
      const typeValue = cachedClient?.ecSiteType || storedEcSiteType;
      if (!typeValue) return;

      const typeNumber = parseInt(String(typeValue), 10);

      // 2. 動的URLの取得
      let dynamicUrl = cachedClient?.ecSiteCouponAdminUrl || null;

      // APIから直接取得する処理 (awaitを使用するため async関数内で実行)
      if (!dynamicUrl && [5, 6].includes(typeNumber) && clientId) {
        try {
          const { data } = await dataProvider.getOne('settings', { id: clientId });

          // 取得したデータで上書き
          if (data && data.ecSiteCouponAdminUrl) {
            dynamicUrl = data.ecSiteCouponAdminUrl;
          }
        } catch (error) {}
      }

      // 末尾スラッシュ除去
      const cleanDynamicUrl = dynamicUrl ? dynamicUrl.replace(/\/$/, '') : null;
      let url: string | null = null;

      switch (typeNumber) {
        case 1: // Shopify
          url = 'https://accounts.shopify.com/store-login';
          break;
        case 2: // BASE
          url = 'https://admin.thebase.com/users/login';
          break;
        case 3: // STORES
          url = 'https://id.stores.jp/login';
          break;
        case 4: // カラーミーショップ
          url = 'https://admin.shop-pro.jp/';
          break;
        case 5: // EC-CUBE
          url = `${cleanDynamicUrl}`;
          break;
        case 6: // futureshop
          url = `${cleanDynamicUrl}`;
          break;
        case 7: // ショップサーブ
          url = 'https://kanri.shopserve.jp/';
          break;
        case 8: // makeshop
          url = 'https://console.makeshop.jp/login';
          break;
        // 対応していない場合は何も表示しない
        default:
          url = null;
      }
      setEcSiteAdminUrl(url);
    };
    determineUrl();
  }, [cachedClient, storedEcSiteType, clientId, dataProvider]);

  // ■ レコードの正規化 (保存ボタン制御のため)
  // 時間付きのISO文字列を、日付のみ(yyyy-MM-dd)に変換してフォームに渡す
  const normalizedRecord = useMemo(() => {
    if (!record) return undefined;
    return {
      ...record,
      expirationDate: record.expirationDate ? format(new Date(record.expirationDate), 'yyyy-MM-dd') : null
    };
  }, [record]);

  // ■ 契約終了日のバリデーション
  const validateContractEndDate = (value: any) => {
    if (!value) return undefined; // required()に任せる

    // cachedClient は親でロード済みなので null チェックは念のため程度
    if (!cachedClient || !cachedClient.closingDate) return undefined;

    const inputDate = new Date(value);
    const tomorrow = startOfDay(addDays(new Date(), 1));

    // 1. 翌日以降チェック
    if (inputDate < tomorrow) {
      const formattedTomorrow = format(tomorrow, 'yyyy/MM/dd');
      return `${formattedTomorrow}以降の日付を選択してください`;
    }

    // 2. 締日チェック
    const closingDateSetting = Number(cachedClient.closingDate);

    // パターンA: 末日締め (99) の場合
    if (closingDateSetting === 99) {
      const lastDay = lastDayOfMonth(inputDate);
      if (!isSameDay(inputDate, lastDay)) {
        return '契約終了日は締日（末日）に合わせてください';
      }
    }
    // パターンB: 日付指定 (例: 20日) の場合
    else if (!isNaN(closingDateSetting)) {
      const inputDay = getDate(inputDate);
      if (inputDay !== closingDateSetting) {
        return `契約終了日は締日（${closingDateSetting}日）に合わせてください`;
      }
    }

    return undefined;
  };

  const handleSubmit = async (data: FieldValues) => {
    try {
      const { applyStatus, invalidFlag, partnerReward, expirationDate, rewardPeriodFrag, rewardPeriodMonths, isCompany } =
        data as {
          applyStatus: number;
          invalidFlag: number;
          partnerReward: string;
          expirationDate: Date | string;
          rewardPeriodFrag: number;
          rewardPeriodMonths?: number;
          isCompany?: boolean;
        };

      // ■ 日付変換ロジック
      // 入力された日付の「一日の終わり（23:59:59.999）」を設定してからISO文字列に変換
      let formattedExpirationDate = expirationDate;
      if (expirationDate) {
        const dateObj = new Date(expirationDate);
        const endOfDayDate = endOfDay(dateObj);
        formattedExpirationDate = endOfDayDate.toISOString();
      }

      // ■ 送信データの補正（disabled 時に react-hook-form の仕様によりデータから除外される問題への対策）
      // 2次パートナーなどのため非活性（disabled）になっている場合、既存レコードの値をセットして送信します。
      const finalPartnerReward = partnerReward !== undefined && partnerReward !== null
        ? partnerReward
        : (record?.partnerReward !== undefined && record?.partnerReward !== null ? String(record.partnerReward) : '');

      const finalIsCompany = isCompany !== undefined && isCompany !== null
        ? isCompany
        : (record?.isCompany !== undefined && record?.isCompany !== null ? record.isCompany : false);

      await dataProvider.update('affiliates', {
        id: record.id,
        data: {
          applyStatus,
          invalidFlag,
          partnerReward: finalPartnerReward,
          expirationDate: formattedExpirationDate,
          rewardPeriodFrag: Number(rewardPeriodFrag),
          rewardPeriodMonths: Number(rewardPeriodFrag) === 1 && rewardPeriodMonths ? Number(rewardPeriodMonths) : null,
          isCompany: finalIsCompany
        },
        previousData: record
      });

      // Case 1: 「申請中(1)」から「承認(2)」にステータスが変更された場合
      if (record.applyStatus === 1 && applyStatus === 2) {
        refresh();
        // クーポン手動作成が必要な場合のみ、管理画面での発行を促すメッセージを表示
        const message = isCouponManualCreationRequired(storedEcSiteType)
          ? '承認しました。\n画面の表示に従って、ECサイトの管理画面からクーポン発行をお願いします。'
          : '承認しました。';
        notify(message, {
          type: 'success',
          multiLine: isCouponManualCreationRequired(storedEcSiteType),
          autoHideDuration: 5000
        });
        return;
      }

      // Case 2: 「申請中(1)」から「却下(3)」にステータスが変更された場合
      if (record.applyStatus === 1 && applyStatus === 3) {
        notify('却下しました。', {
          type: 'info'
        });
        redirect('list', 'affiliates');
        return;
      }

      // Case 3: それ以外の更新の場合 (例: invalidFlagの変更、報酬の変更など)
      notify('更新しました。', { type: 'success' });
      refresh();
    } catch (error) {
      handleError(error);
    }
  };

  if (!record) {
    return null;
  }

  return (
    <Box>
      <SimpleShowLayout sx={{ pb: 0 }}>
        <EnumTextField source="applyStatus" translationKeyPrefix="values.applyStatus" />
        <TextField source="partnerId" />
        <FunctionField
          label={translate('resources.affiliates.fields.partnerClass')}
          render={record => record.parentPartnerId ? '2次' : '1次'}
        />
        <ReferenceField source="parentPartnerId" reference="partners" emptyText='-' label={translate('resources.affiliates.fields.parentPartner')}>
          <TextField source="name" />
        </ReferenceField>
        <TextField source="id" label={translate('resources.affiliates.fields.id')} />
        {record.applyStatus === 2 && (
          <UrlField source="affiliateUrl" label={translate('resources.affiliates.fields.affiliateUrl')} />
        )}
        <TextField source="name" />
        <EnumTextField source="partnerType" translationKeyPrefix={'values.partnerType'} />
        <EmailField source="email" />
        <UrlField source="website" />
        {record.partnerType === 2 && <TextField source="tel" />}
        {record.partnerType === 1 && <TextField source="mobilePhone" />}
        <PostalCodeField source="zipCode" />
        <TextField source="prefecture" />
        <TextField source="city" />
        <TextField source="address1" />
        <TextField source="address2" />
        <TextField source="invoiceNumber" />
        {record.partnerType === 2 && <TextField source="dunsNumber" />}
        <DateField source="createdAt" label={translate('resources.affiliates.fields.createdAt')} showTime />
        <DateField
          source="approvalStatusChangedAt"
          label={translate('resources.affiliates.fields.approvalStatusChangedAt')}
          showTime
        />
        <ReferenceField
          source="approvalStatusChanger"
          reference="references/accounts"
          label={translate('resources.affiliates.fields.approvalStatusChanger')}
          link={false}
        >
          <TextField source="name" />
        </ReferenceField>
        <EnumTextField source="accountInvalidFlag" translationKeyPrefix="values.accountInvalidFlag" />
      </SimpleShowLayout>
      <Edit
        sx={{
          '& .RaEdit-card': {
            border: 'none',
            boxShadow: 'none'
          }
        }}
        mutationMode="pessimistic"
        mutationOptions={{ onError: handleError }}
        title=" "
      >
        <SimpleForm
          // normalizedRecord を使用し、日付文字列をkeyにして再描画を制御
          record={normalizedRecord}
          key={normalizedRecord?.expirationDate}
          onSubmit={handleSubmit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
          sx={{ pt: 0 }}
          toolbar={<CustomToolbar />}
        >
          <DateInput
            source="expirationDate"
            label={translate('resources.affiliates.fields.expirationDate')}
            // normalizedRecordを使っているのでdefaultValueは自動設定される
            validate={[required(), validateContractEndDate]}
          />
          <PercentNumberInput
            source="partnerReward"
            validate={required()}
            disabled={record?.parentPartnerId !== null && record?.parentPartnerId !== undefined}
          />
          {/* {(record?.parentPartnerId === null) &&
            <Box display="flex" flexDirection="column" sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {translate('resources.affiliates.fields.introducerReward')}
              </Typography>
              <PercentNumberField source="introducerReward" />
            </Box>
          } */}

          {/* 継続報酬支払い期間設定エリア */}
          <NumericRadioButtonGroupInput
            source="rewardPeriodFrag"
            label="継続報酬支払い期間"
            defaultValue={0} // デフォルトは無期
            row={true}
            choices={[
              { id: 1, name: '有期' },
              { id: 0, name: '無期' }
            ]}
            sx={{ mb: 0 }}
          />

          {/* 有期(1)が選択された場合のみ表示 */}
          <FormDataConsumer>
            {({ formData, ...rest }) => {
              // 共通の警告メッセージ定義
              const WarningText = (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0, mb: 2 }}>
                  ※顧客当たりの継続報酬期間です。この報酬期間内に再度ご購入いただいた場合、対象の注文をパートナーの成果として捕捉し注文管理画面に情報を表示します。
                </Typography>
              );

              // 有期(1)が選択された場合：入力欄を表示し、その下に警告を表示
              if (formData.rewardPeriodFrag === 1) {
                return (
                  <Box sx={{ ml: 2, mt: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <NumberInput
                        source="rewardPeriodMonths"
                        label="月数"
                        min={1}
                        max={60}
                        validate={[required(), minValue(1)]}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">ヶ月</InputAdornment>
                        }}
                        onKeyDown={(event: React.KeyboardEvent) => {
                          if (['+', '-', 'e', 'E', '.'].includes(event.key)) {
                            event.preventDefault();
                          }
                        }}
                        sx={{ width: 130 }}
                      />
                      <Typography variant="body2" color="error" sx={{ fontWeight: 'bold' }}>
                        ※月数は1~60の値で入力ください。
                      </Typography>
                    </div>
                    {/* 入力欄のすぐ下に表示 */}
                    {WarningText}
                  </Box>
                );
              }

              // 無期(0)の場合：ラジオボタンのすぐ下に表示
              return WarningText;
            }}
          </FormDataConsumer>
          {(record?.parentPartnerId === null || record?.parentPartnerId === undefined) && (
            <BooleanInput 
              source="isCompany" 
              label="2次パートナー招待を許可" 
              format={(value: boolean) => !value}
              parse={(value: boolean) => !value}
              fullWidth 
            />
          )}
          <Box
            sx={{
              width: '100%',
              border: '1px solid rgba(0, 0, 0, 0.1)',
              margin: '40px 0px'
            }}
          ></Box>

          {record?.applyStatus === 1 && (
            <>
              <Typography variant="body1" gutterBottom>
                上記の申請内容を確認して、承認または却下を選択してください。
              </Typography>
              <NumericRadioButtonGroupInput source="applyStatus" choices={EDIT_APPLY_STATUS} validate={required()} />
            </>
          )}
          {record?.applyStatus === 2 && (
            <>
              {/*  クーポン機能がサポートされている場合のみ表示 */}
              {isCouponSupported(storedEcSiteType) && (
                <>
                  <Typography variant="body1" gutterBottom>
                    {isCouponManualCreationRequired(storedEcSiteType)
                      ? 'クーポンコードを作成する際は以下のコードをコピーしてECサイトの管理画面でクーポンを作成してください。'
                      : 'ECサイトの管理画面で以下のクーポンコードの自動生成を行いました。'}
                  </Typography>
                  {/* ecSiteAdminUrl が null でない場合のみURLリンクを表示 (タイプ 6, 7 では表示されない) */}
                  {ecSiteAdminUrl && (
                    <Box sx={{ my: 1 }}>
                      <Typography variant="body2" component="span" sx={{ mr: 0.5 }}>
                        ECサイト管理画面URL:
                      </Typography>
                      <MuiLink href={ecSiteAdminUrl} target="_blank" rel="noopener noreferrer" variant="body2">
                        {ecSiteAdminUrl}
                      </MuiLink>
                    </Box>
                  )}
                  <SimpleShowLayout sx={{ pb: 0 }}>
                    <CopyableTextField source="couponCode" label={translate('resources.affiliates.fields.coupon')} />
                  </SimpleShowLayout>
                </>
              )}

              {/*applyStatus === 2 であれば常に表示 */}
              <Typography variant="body1" gutterBottom sx={{ mt: isCouponSupported(storedEcSiteType) ? 2 : 0 }}>
                {/* クーポンブロックが非表示になる場合、マージントップを0にする */}
                以下のボタンでアフィリエイトステータスを切り替えることが可能です。
              </Typography>
              <NumericRadioButtonGroupInput source="invalidFlag" choices={INVALD_FLAG} />
              <Typography variant="caption" color="error" sx={{ mt: -3, display: 'block' }}>
                無効にした場合、アフィリエイトステータスが無効時に発生した紹介注文は、successfeeに取り込まれません。
              </Typography>
            </>
          )}
        </SimpleForm>
      </Edit>
    </Box>
  );
};

const Title = () => {
  const record = useRecordContext();
  return record ? <span>パートナー管理 #{record.name}</span> : null;
};

// ▼ 親コンポーネントでデータ取得を一括管理
export const AffiliateEdit = () => {
  const clientId = localStorage.getItem('clientId');

  // 店舗設定データの取得
  const { data: cachedClient, isLoading } = useGetOne('settings', { id: clientId }, { enabled: !!clientId });

  // データロード中は Loading コンポーネントを表示して Content を描画しない
  // これにより、Content が描画されるときは cachedClient が確実に存在するため、
  // バリデーションエラーのすり抜けが発生しない
  if (isLoading) {
    return <Loading />;
  }

  return (
    <Show title={<Title />} resource="affiliates" actions={false} sx={{ mb: '1rem' }}>
      <AffiliateEditContent cachedClient={cachedClient} />
    </Show>
  );
};

export default { AffiliatesList, AffiliateEdit };
